import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { siteName, siteUrl } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEO title={`文章未找到 — ${siteName}`} description="您访问的文章不存在或已被移除。" canonicalUrl={siteUrl} />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <span className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    image: article.coverImage,
    url: `${siteUrl}/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: siteName,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${siteUrl}/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${siteUrl}/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} — ${siteName}`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteUrl}/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="px-4 md:px-6 pb-14">
            <div className="w-full h-56 md:h-80 rounded-2xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={`${article.title} - 中文字幕 日韩`}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-medium">
                {article.categoryName}
              </span>
              <span className="text-sm text-foreground-500">发布于 {article.publishDate}</span>
              <span className="text-sm text-foreground-500">更新于 {article.updateDate}</span>
              <span className="text-sm text-foreground-500 flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-background-200">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-user-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                <p className="text-xs text-foreground-500">作者</p>
              </div>
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200">
              <span className="text-sm text-foreground-500 mr-2 self-center">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <aside className="mt-14 pt-10 border-t border-background-200">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group flex flex-col rounded-xl bg-background-100 overflow-hidden cursor-pointer"
                    >
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </aside>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}